import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { appCurrentBranding } from '../../../config/branding';
import { branding } from '../../../config/enum';
import {
  UserAddInterFace,
  UserInterFace,
  UserParameters,
} from '../../../interface/users-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { fetchAllListRoles } from '../../../redux/slices/RolesManagementSlice';
import { fetchAllUsers } from '../../../redux/slices/UsersManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import {
  emailValidation,
  passwordValidation,
  phoneNumberValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import userApi from '../../../utils/api/users';

interface AddUserProperties {
  closeModal: () => void;
  currentDate?: UserInterFace;
  completedTask: (message: string, type: string) => void;
}

/**
 * Add User page function component
 * @param {any} properties - Properties
 * @returns {any} Add user component
 */
function AddUser(properties: AddUserProperties) {
  const {
    closeModal,
    currentDate = {
      id: '',
      _id: '',
      name: '',
      date: '',
      accountId: '',
      countryCode: '',
      email: '',
      phoneNumber: '',
      role: '',
      status: false,
      createdAt: '',
    },
    completedTask,
  } = properties;
  const { t } = useTranslation('userRole');
  const dispatch = useAppDispatch();
  const { page, limit } = useAppSelector((state) => state.users);
  const { rolesList } = useAppSelector((state) => state.roles);

  const [disabled, setDisabled] = useState<boolean>(false);
  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserAddInterFace>({
    role: currentDate.role || '',
    name: currentDate.name || '',
    email: currentDate.email || '',
    password: '',
    rePassword: '',
    phone: currentDate?.phoneNumber || '',
    countryCode: currentDate?.countryCode || (appCurrentBranding === branding.VOLTIE ? '1' : '966'),
  });

  /**
   * Fetch Users list function
   */
  const fetchData = () => {
    dispatch(fetchAllListRoles());
  };

  /**
   * Handle the input change function
   * @param {string} name - Name
   * @param {any} value - Value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setUserDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the submit function
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    let errorMessage = false;
    if (!currentDate.id) {
      if (
        userDetails.role === '' ||
        !stringValidation(1, 100, userDetails.name) ||
        userDetails.email === '' ||
        !passwordValidation(userDetails.password) ||
        userDetails.phone === '' ||
        userDetails.countryCode === '' ||
        !emailValidation(userDetails.email) ||
        !phoneNumberValidation(userDetails.phone) ||
        userDetails.rePassword !== userDetails.password
      ) {
        errorMessage = true;
      }
      if (!errorMessage) {
        setDisabled(true);
        const request: UserParameters = {
          name: userDetails.name,
          roles: [userDetails.role],
          email: userDetails.email.toLowerCase(),
          password: userDetails.password,
          phoneNumber: userDetails.phone,
          countryCode: userDetails.countryCode,
        };
        userApi
          .createUser(request)
          .then(() => {
            setDisabled(false);
            completedTask(t('userAddedSuccessfully'), 'user');
            closeModal(); // Close side panel handled by parent ideally, but this ensure cleanup
          })
          .catch((error) => {
            setDisabled(false);
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: error?.response?.data?.code,
              }),
            );
          });
      }
    } else {
      if (
        userDetails.role === '' ||
        !stringValidation(1, 100, userDetails.name) ||
        userDetails.email === '' ||
        userDetails.phone === '' ||
        userDetails.countryCode === '' ||
        !emailValidation(userDetails.email) ||
        !phoneNumberValidation(userDetails.phone)
      ) {
        errorMessage = true;
      }
      if (!errorMessage) {
        setDisabled(true);
        const request: UserParameters = {
          name: userDetails.name,
          roles: [userDetails.role],
          email: userDetails.email.toLowerCase(),
          phoneNumber: userDetails.phone,
          countryCode: userDetails.countryCode,
        };
        userApi
          .editUser(currentDate.id, request)
          .then(() => {
            setDisabled(false);
            completedTask(t('userUpdatedSuccessfully'), 'user');
            dispatch(
              fetchAllUsers({
                page,
                limit,
              }),
            );
            closeModal();
          })
          .catch((error) => {
            setDisabled(false);
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: error?.response?.data?.code,
              }),
            );
          });
      }
    }
  };

  useEffect(() => {
    if (rolesList.length === 0) {
      fetchData();
    }
  }, []);

  return (
    <div className="add-user-side-panel">
      <div className="panel-header">
        <h3>{currentDate.id ? t('editUser') : t('addUser')}</h3>
      </div>

      <div
        style={{
          overflowY: 'scroll',
          height: window.innerHeight >= 700 ? 347 : 352,
        }}>
        <div className="add-user-group">
          <div className="profile-card">
            <div className="edit-fields-container">
              <div className="info-row-input">
                <SelectFiled
                  name="role"
                  inputLabel={t('role')}
                  selectedValue={userDetails.role}
                  placeholder={t('selectRole')}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={showFormErrors && userDetails.role === ''}
                  errorMessage={t('roleRequired')}
                  options={rolesList}
                />
                <CustomInput
                  name="name"
                  inputLabel={t('name')}
                  type="text"
                  placeholder={t('enterName')}
                  value={userDetails.name}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={showFormErrors && !stringValidation(1, 100, userDetails.name)}
                  errorMessage={t('nameRequired')}
                />
              </div>
              <div className="info-row-input">
                <CustomInput
                  name="email"
                  inputLabel={t('email')}
                  type="email"
                  placeholder={t('enterEmail')}
                  value={userDetails.email}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={
                    showFormErrors &&
                    (userDetails.email === '' || !emailValidation(userDetails.email))
                  }
                  errorMessage={t('emailInvalid')}
                />
                <CustomPhoneInput
                  name="phone"
                  inputLabel={t('phoneNumber')}
                  type="number"
                  placeholder={t('enterPhone')}
                  value={userDetails.phone}
                  value1={userDetails.countryCode}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={
                    showFormErrors &&
                    (userDetails.phone === '' || !phoneNumberValidation(userDetails.phone))
                  }
                  errorMessage={t('phoneInvalid')}
                />
              </div>
              {!currentDate.id && (
                <div className="info-row-input">
                  <CustomInput
                    name="password"
                    inputLabel={t('password')}
                    type="password"
                    placeholder={t('enterPassword')}
                    value={userDetails.password}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && !passwordValidation(userDetails.password)}
                    errorMessage={
                      userDetails.password.length === 0
                        ? t('passwordRequired')
                        : t('passwordInvalid')
                    }
                  />
                  <CustomInput
                    name="rePassword"
                    inputLabel={t('reEnterPassword')}
                    type="password"
                    placeholder={t('enterPassword')}
                    value={userDetails.rePassword}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={
                      showFormErrors &&
                      (userDetails.rePassword === '' ||
                        userDetails.password !== userDetails.rePassword)
                    }
                    errorMessage={
                      userDetails.rePassword.length === 0
                        ? t('rePasswordRequired')
                        : t('passwordsDoNotMatch')
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="footer-actions" style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
          <MyButton label={t('cancel')} buttonType="outline" onClickFunc={closeModal} />
          <MyButton
            label={currentDate.id ? t('update') : t('submit')}
            buttonType="submit"
            disableButton={disabled}
            loading={disabled}
            onClickFunc={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}

AddUser.defaultProps = {
  currentDate: {},
};

export default AddUser;

// import { branding } from '../../../config/enum';
// import {
//   UserAddInterFace,
//   UserInterFace,
//   UserParameters,
// } from '../../../interface/users-management.interface';
// import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
// import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
// import { fetchAllListRoles } from '../../../redux/slices/RolesManagementSlice';
// import { fetchAllUsers } from '../../../redux/slices/UsersManagementSlice';
// import MyButton from '../../../shared-components/button/Button';
// import CustomInput from '../../../shared-components/custom-input/CustomInput';
// import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
// import SelectFiled from '../../../shared-components/select-field/SelectField';
// import {
//   emailValidation,
//   passwordValidation,
//   phoneNumberValidation,
//   stringValidation,
// } from '../../../shared-functions/Validation';
// import userApi from '../../../utils/api/users';

// /**
//  * Add User page function component
//  */
// function AddUser({
//   closeModal,
//   currentDate = {
//     id: '',
//     _id: '',
//     name: '',
//     date: '',
//     accountId: '',
//     countryCode: '',
//     email: '',
//     phoneNumber: '',
//     role: '',
//     status: false,
//     createdAt: '',
//   },
//   completedTask,
// }: {
//   closeModal: () => void;
//   currentDate?: UserInterFace;
//   completedTask: (message: string, type: string) => void;
// }) {
//   const { t } = useTranslation('userRole');
//   const dispatch = useAppDispatch();
//   const { page, limit } = useAppSelector((state) => state.users);
//   const { rolesList } = useAppSelector((state) => state.roles);

//   const [disabled, setDisabled] = useState<boolean>(false);
//   const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
//   const [userDetails, setUserDetails] = useState<UserAddInterFace>({
//     role: currentDate.role || '',
//     name: currentDate.name || '',
//     email: currentDate.email || '',
//     password: '',
//     rePassword: '',
//     phone: currentDate?.phoneNumber || '',
//     countryCode: currentDate?.countryCode || (appCurrentBranding === branding.VOLTIE ? '1' : '966'),
//   });

//   /**
//    * Fetch Users list function
//    */
//   const fetchData = () => {
//     dispatch(fetchAllListRoles());
//   };

//   /**
//    * Handle the input change function
//    * @param name
//    * @param value
//    */
//   const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
//     setUserDetails((previous) => ({
//       ...previous,
//       [name]: value,
//     }));
//   };

//   /**
//    * Handle the submit function
//    */
//   const handleSubmit = () => {
//     setShowFormErrors(true);
//     let errorMessage = false;
//     if (!currentDate.id) {
//       if (
//         userDetails.role === '' ||
//         !stringValidation(1, 100, userDetails.name) ||
//         userDetails.email === '' ||
//         !passwordValidation(userDetails.password) ||
//         userDetails.phone === '' ||
//         userDetails.countryCode === '' ||
//         !emailValidation(userDetails.email) ||
//         !phoneNumberValidation(userDetails.phone) ||
//         userDetails.rePassword !== userDetails.password
//       ) {
//         errorMessage = true;
//       }
//       if (!errorMessage) {
//         setDisabled(true);
//         const request: UserParameters = {
//           name: userDetails.name,
//           role: userDetails.role,
//           email: userDetails.email.toLowerCase(),
//           password: userDetails.password,
//           phoneNumber: userDetails.phone,
//           countryCode: userDetails.countryCode,
//         };
//         userApi
//           .createUser(request)
//           .then(() => {
//             setDisabled(false);
//             completedTask(t('userAddedSuccessfully'), 'user');
//           })
//           .catch((error) => {
//             setDisabled(false);
//             dispatch(
//               setNotificationMessage({
//                 message: error?.response?.data?.message,
//                 status: true,
//                 type: 'error',
//                 code: error?.response?.data?.code,
//               }),
//             );
//           });
//       }
//     } else {
//       if (
//         userDetails.role === '' ||
//         !stringValidation(1, 100, userDetails.name) ||
//         userDetails.email === '' ||
//         userDetails.phone === '' ||
//         userDetails.countryCode === '' ||
//         !emailValidation(userDetails.email) ||
//         !phoneNumberValidation(userDetails.phone)
//       ) {
//         errorMessage = true;
//       }
//       if (!errorMessage) {
//         setDisabled(true);
//         const request: UserParameters = {
//           name: userDetails.name,
//           role: userDetails.role,
//           email: userDetails.email.toLowerCase(),
//           phoneNumber: userDetails.phone,
//           countryCode: userDetails.countryCode,
//         };
//         userApi
//           .editUser(currentDate.id, request)
//           .then(() => {
//             setDisabled(false);
//             completedTask(t('userUpdatedSuccessfully'), 'user');
//             dispatch(
//               fetchAllUsers({
//                 page,
//                 limit,
//               }),
//             );
//           })
//           .catch((error) => {
//             setDisabled(false);
//             dispatch(
//               setNotificationMessage({
//                 message: error?.response?.data?.message,
//                 status: true,
//                 type: 'error',
//                 code: error?.response?.data?.code,
//               }),
//             );
//           });
//       }
//     }
//   };

//   useEffect(() => {
//     if (rolesList.length === 0) {
//       fetchData();
//     }
//   }, []);

//   return (
//     <div className="add-account-form">
//       <div className="form-body">
//         <div className="form-field">
//           <SelectFiled
//             name="role"
//             inputLabel={t('role')}
//             selectedValue={userDetails.role}
//             placeholder={t('selectRole')}
//             onChangeValue={handleInputChange}
//             valueRequired
//             error={showFormErrors && userDetails.role === ''}
//             errorMessage={t('roleRequired')}
//             options={rolesList}
//           />
//         </div>
//         <div className="form-field">
//           <CustomInput
//             name="name"
//             inputLabel={t('name')}
//             type="text"
//             placeholder={t('enterName')}
//             value={userDetails.name}
//             onChangeValue={handleInputChange}
//             valueRequired
//             error={showFormErrors && !stringValidation(1, 100, userDetails.name)}
//             errorMessage={t('nameRequired')}
//           />
//         </div>
//         <div className="form-field">
//           <CustomInput
//             name="email"
//             inputLabel={t('email')}
//             type="email"
//             placeholder={t('enterEmail')}
//             value={userDetails.email}
//             onChangeValue={handleInputChange}
//             valueRequired
//             error={
//               showFormErrors && (userDetails.email === '' || !emailValidation(userDetails.email))
//             }
//             errorMessage={t('emailInvalid')}
//           />
//         </div>
//         <div className="form-field">
//           <CustomPhoneInput
//             name="phone"
//             inputLabel={t('phoneNumber')}
//             type="number"
//             placeholder={t('enterPhone')}
//             value={userDetails.phone}
//             value1={userDetails.countryCode}
//             onChangeValue={handleInputChange}
//             valueRequired
//             error={
//               showFormErrors &&
//               (userDetails.phone === '' || !phoneNumberValidation(userDetails.phone))
//             }
//             errorMessage={t('phoneInvalid')}
//           />
//         </div>
//         {!currentDate.id && (
//           <>
//             <div className="form-field">
//               <CustomInput
//                 name="password"
//                 inputLabel={t('password')}
//                 type="password"
//                 placeholder={t('enterPassword')}
//                 value={userDetails.password}
//                 onChangeValue={handleInputChange}
//                 valueRequired
//                 error={showFormErrors && !passwordValidation(userDetails.password)}
//                 errorMessage={
//                   userDetails.password.length === 0 ? t('passwordRequired') : t('passwordInvalid')
//                 }
//               />
//             </div>
//             <div className="form-field">
//               <CustomInput
//                 name="rePassword"
//                 inputLabel={t('reEnterPassword')}
//                 type="password"
//                 placeholder={t('enterPassword')}
//                 value={userDetails.rePassword}
//                 onChangeValue={handleInputChange}
//                 valueRequired
//                 error={
//                   showFormErrors &&
//                   (userDetails.rePassword === '' || userDetails.password !== userDetails.rePassword)
//                 }
//                 // errorMessage="Password and Re-password is also same"
//                 errorMessage={
//                   userDetails.rePassword.length === 0
//                     ? t('rePasswordRequired')
//                     : t('passwordsDoNotMatch')
//                 }
//               />
//             </div>
//           </>
//         )}
//         <div className="form-field table-bottom-button-list form-bottom-button end">
//           <div className="table-button-item">
//             <MyButton label={t('cancel')} buttonType="back" onClickFunc={closeModal} />
//           </div>
//           <div className="table-button-item">
//             <MyButton
//               label={t('submit')}
//               buttonType="submit"
//               disabled={disabled}
//               loading={disabled}
//               onClickFunc={handleSubmit}
//             />
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// AddUser.defaultProps = {
//   currentDate: {},
// };

// export default AddUser;
